import { useFollowedSite } from '../../../contexts/SiteContext';
import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight, Minus, RefreshCw, ExternalLink, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Button } from '@verlo/shared/components/ui/button';
import { reportsIntelApi } from '@verlo/shared/services/api';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import ProfitWaterfall from '../../../components/reports/ProfitWaterfall';
import TrendChart from '../../../components/reports/TrendChart';
import ExplainVarianceBubble from '../../../components/reports/ExplainVarianceBubble';
import SeeMoreLink from '../../../components/reports/SeeMoreLink';
import { Segmented } from '@verlo/shared/components/ui/segmented';

const PERIODS = [
  { key: 'daily',   label: 'Daily' },
  { key: 'weekly',  label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
];

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

const stepDate = (period, dateStr, dir) => {
  const d = new Date(dateStr || new Date().toISOString().slice(0, 10));
  if (period === 'daily') d.setUTCDate(d.getUTCDate() + dir);
  else if (period === 'weekly') d.setUTCDate(d.getUTCDate() + dir * 7);
  else if (period === 'monthly') d.setUTCMonth(d.getUTCMonth() + dir);
  return d.toISOString().slice(0, 10);
};

const HeroKpi = ({ label, value, deltaPrev, deltaForecast, suffix, kpi, range, siteLabel, sharedContext, lowerIsBetter, seeMoreTo }) => {
  const isGoodPrev = deltaPrev === null || deltaPrev === undefined
    ? null
    : lowerIsBetter ? deltaPrev < 0 : deltaPrev > 0;
  const deltaColor = isGoodPrev === null ? 'text-ink-faint' : isGoodPrev ? 'text-signal-ok' : 'text-signal-stop';
  const DeltaIcon = deltaPrev === null || deltaPrev === undefined ? Minus : deltaPrev > 0 ? ArrowUpRight : deltaPrev < 0 ? ArrowDownRight : Minus;
  return (
    <div className="bg-white border border-hairline rounded-lg p-5 relative group hover:border-ink-faint hover:shadow-[0_8px_30px_-12px_rgba(32,27,95,0.18)] transition-all flex flex-col" data-testid={`financial-kpi-${kpi.key}`}>
      <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-faint">{label}</span>
      <div className="flex items-baseline gap-3 flex-wrap mt-2">
        <span className="font-mono-display text-3xl md:text-4xl font-medium text-ink">
          {suffix === '%' ? `${value.toFixed(1)}%` : fmtGBP(value)}
        </span>
        {deltaPrev !== null && deltaPrev !== undefined && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${deltaColor}`}>
            <DeltaIcon size={13} />
            {Math.abs(deltaPrev).toFixed(1)}%
          </span>
        )}
      </div>
      <p className="text-[12.5px] text-ink-faint mt-1">
        vs previous · forecast Δ{' '}
        <span className={`font-semibold ${deltaForecast > 0 ? 'text-signal-ok' : deltaForecast < 0 ? 'text-signal-stop' : 'text-ink-faint'}`}>
          {deltaForecast === null || deltaForecast === undefined ? '—' : `${deltaForecast > 0 ? '+' : ''}${deltaForecast.toFixed(1)}%`}
        </span>
      </p>
      <ExplainVarianceBubble kpi={kpi} range={range} siteLabel={siteLabel} context={sharedContext} />
      <div className="mt-3 pt-3 border-t border-hairline flex justify-end">
        <SeeMoreLink to={seeMoreTo || '/back-office/reports/pnl'} what={label} testId={`financial-see-more-${kpi.key}`} />
      </div>
    </div>
  );
};

const FinancialReportsPage = () => {
  const [sites, setSites] = useState([]);
  // '' = every site this user can read; the API omits the param (S1-4).
  const [site, setSite] = useFollowedSite('');
  const [period, setPeriod] = useState('daily');
  const [date, setDate] = useState(null); // ISO YYYY-MM-DD; null = today
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const siteLabel = useMemo(() => sites.find((s) => s.id === site)?.name || site, [sites, site]);

  useEffect(() => {
    reportsIntelApi.listSites().then((r) => setSites(r.data.sites || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reportsIntelApi
      .getFinancialPnl(period, site, date)
      .then((res) => { if (!cancelled) setData(res.data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, site, date]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await reportsIntelApi.getFinancialPnl(period, site, date);
      setData(res.data);
    } finally {
      setRefreshing(false);
    }
  };

  const sharedContext = useMemo(() => data ? {
    period,
    revenue: data.current.revenue,
    labour: data.current.labour,
    cogs: data.current.cogs,
    other: data.current.other,
    ebitda: data.current.ebitda,
    site_id: site,
  } : null, [data, period, site]);

  return (
    <ReportsLayout>
      {/* Header controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger className="h-9 w-[230px] bg-white border-hairline" data-testid="fin-site-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sites.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`fin-site-option-${s.id}`}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Segmented
            value={period}
            onChange={setPeriod}
            options={PERIODS.map((p) => ({ value: p.key, label: p.label, testId: `fin-period-${p.key}` }))}
            data-testid="fin-period-switcher"
          />

          <div className="inline-flex items-center gap-1 bg-white border border-hairline rounded-lg p-0.5">
            <Button
              variant="ghost" size="icon" className="h-7 w-7"
              onClick={() => setDate(stepDate(period, date || new Date().toISOString().slice(0, 10), -1))}
              data-testid="fin-date-prev"
              title="Previous period"
            >
              <ChevronLeft size={14} />
            </Button>
            <span className="text-[12px] font-semibold text-ink-soft px-2 min-w-[160px] text-center" data-testid="fin-date-label">
              {data?.label || '—'}
            </span>
            <Button
              variant="ghost" size="icon" className="h-7 w-7"
              onClick={() => setDate(stepDate(period, date || new Date().toISOString().slice(0, 10), 1))}
              data-testid="fin-date-next"
              title="Next period"
            >
              <ChevronRight size={14} />
            </Button>
          </div>

          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={() => setDate(null)} data-testid="fin-today-btn">
            Today
          </Button>
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={refresh} data-testid="fin-refresh-btn">
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/back-office/pnl"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-soft hover:text-verlo bg-white border border-hairline rounded-lg px-3 py-2 transition-colors"
            data-testid="fin-deeplink-pnl"
          >
            <BarChart3 size={12} />
            Legacy P&amp;L
            <ExternalLink size={10} className="opacity-50" />
          </Link>
        </div>
      </div>

      {loading || !data ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-32 bg-white border border-hairline rounded-lg animate-pulse" />
          ))}
        </div>
      ) : data.is_empty ? (
        <div className="bg-white border border-hairline rounded-lg p-12 text-center" data-testid="fin-empty">
          <BarChart3 size={28} className="text-ink-faint mx-auto mb-3" />
          <h3 className="text-[14px] font-semibold text-ink">No P&amp;L data for {data.label}</h3>
          <p className="text-[12px] text-ink-soft mt-1">No sales recorded in this period.</p>
        </div>
      ) : (
        <>
          {/* Hero KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
            <HeroKpi
              label="Revenue"
              value={data.current.revenue}
              deltaPrev={data.lines[0].delta_vs_previous_pct}
              deltaForecast={data.lines[0].delta_vs_forecast_pct}
              kpi={{ key: 'revenue', label: 'Revenue', value: data.current.revenue, previous: data.previous.revenue, target: data.forecast.revenue, format: 'currency' }}
              range={period}
              siteLabel={siteLabel}
              sharedContext={sharedContext}
            />
            <HeroKpi
              label="Gross profit"
              value={data.lines[2].value}
              deltaPrev={data.lines[2].delta_vs_previous_pct}
              deltaForecast={data.lines[2].delta_vs_forecast_pct}
              kpi={{ key: 'gross_profit', label: 'Gross profit', value: data.lines[2].value, previous: data.lines[2].previous, target: data.lines[2].forecast, format: 'currency' }}
              range={period}
              siteLabel={siteLabel}
              sharedContext={sharedContext}
            />
            <HeroKpi
              label="Labour cost"
              value={data.current.labour}
              deltaPrev={data.lines[3].delta_vs_previous_pct}
              deltaForecast={data.lines[3].delta_vs_forecast_pct}
              kpi={{ key: 'labour', label: 'Labour cost', value: data.current.labour, previous: data.previous.labour, target: data.forecast.labour, format: 'currency' }}
              range={period}
              siteLabel={siteLabel}
              sharedContext={sharedContext}
              lowerIsBetter
            />
            <HeroKpi
              label="EBITDA"
              value={data.current.ebitda}
              deltaPrev={data.lines[5].delta_vs_previous_pct}
              deltaForecast={data.lines[5].delta_vs_forecast_pct}
              kpi={{ key: 'ebitda', label: 'EBITDA', value: data.current.ebitda, previous: data.previous.ebitda, target: data.forecast.ebitda, format: 'currency' }}
              range={period}
              siteLabel={siteLabel}
              sharedContext={sharedContext}
            />
          </div>

          {/* Ratio strip */}
          <div className="bg-white border border-hairline rounded-lg p-5 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="fin-ratios">
            <div>
              <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-faint">Gross margin</span>
              <p className="font-mono-display text-2xl text-ink mt-1">{data.ratios.gross_profit_pct}%</p>
            </div>
            <div>
              <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-faint">Labour %</span>
              <p className="font-mono-display text-2xl text-ink mt-1">{data.ratios.labour_pct}%</p>
            </div>
            <div>
              <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-faint">EBITDA margin</span>
              <p className="font-mono-display text-2xl text-ink mt-1">{data.ratios.ebitda_pct}%</p>
            </div>
          </div>

          {/* Waterfall + Trend */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 mb-6">
            <div className="xl:col-span-2 bg-white border border-hairline rounded-lg p-5" data-testid="fin-waterfall-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-[13px] font-semibold text-ink">Profit waterfall</h3>
                  <p className="text-[12.5px] text-ink-soft">Revenue → EBITDA for {data.label}</p>
                </div>
              </div>
              <div className="h-64 md:h-72">
                <ProfitWaterfall steps={data.waterfall} />
              </div>
            </div>
            <div className="bg-white border border-hairline rounded-lg p-5" data-testid="fin-trend-card">
              <div className="mb-3">
                <h3 className="text-[13px] font-semibold text-ink">8-period trend</h3>
                <p className="text-[12.5px] text-ink-soft">Revenue (Verlo Blue) vs EBITDA (emerald)</p>
              </div>
              <div className="h-64 md:h-72">
                <TrendChart trend={data.trend} />
              </div>
            </div>
          </div>

          {/* P&L line table */}
          <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="fin-pnl-table">
            <div className="px-5 py-4 border-b border-hairline flex items-center justify-between">
              <div>
                <h3 className="text-[13px] font-semibold text-ink">P&amp;L detail</h3>
                <p className="text-[12.5px] text-ink-soft">{data.label} · vs previous &amp; forecast</p>
              </div>
            </div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-ink-faint border-b border-hairline">
                  <th className="text-left font-semibold px-5 py-3">Line</th>
                  <th className="text-right font-semibold px-3 py-3">Actual</th>
                  <th className="text-right font-semibold px-3 py-3">Previous</th>
                  <th className="text-right font-semibold px-3 py-3">Δ vs prev</th>
                  <th className="text-right font-semibold px-3 py-3">Forecast</th>
                  <th className="text-right font-semibold px-3 py-3">Δ vs fcst</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((l) => {
                  const lower = l.line === 'COGS' || l.line === 'Labour' || l.line === 'Other costs';
                  const goodPrev = l.delta_vs_previous_pct === null ? null : lower ? l.delta_vs_previous_pct < 0 : l.delta_vs_previous_pct > 0;
                  const goodFcst = l.delta_vs_forecast_pct === null ? null : lower ? l.delta_vs_forecast_pct < 0 : l.delta_vs_forecast_pct > 0;
                  const accent = l.line === 'Gross profit' || l.line === 'EBITDA' ? 'bg-surface-2 font-semibold' : '';
                  return (
                    <tr key={l.line} className={`border-b border-hairline last:border-0 ${accent}`} data-testid={`fin-row-${l.line.toLowerCase().replace(/\s/g, '-')}`}>
                      <td className="px-5 py-3 text-ink">{l.line}</td>
                      <td className="text-right px-3 py-3 font-mono-display text-ink" data-testid={l.line === 'COGS' ? 'fin-cogs' : l.line === 'Labour' ? 'fin-labour' : l.line === 'EBITDA' ? 'fin-net' : undefined}>{fmtGBP(l.value)}</td>
                      <td className="text-right px-3 py-3 font-mono-display text-ink-soft">{fmtGBP(l.previous)}</td>
                      <td className={`text-right px-3 py-3 font-mono-display ${goodPrev === null ? 'text-ink-faint' : goodPrev ? 'text-signal-ok' : 'text-signal-stop'}`}>
                        {l.delta_vs_previous_pct === null ? '—' : `${l.delta_vs_previous_pct > 0 ? '+' : ''}${l.delta_vs_previous_pct.toFixed(1)}%`}
                      </td>
                      <td className="text-right px-3 py-3 font-mono-display text-ink-soft">{fmtGBP(l.forecast)}</td>
                      <td className={`text-right px-3 py-3 font-mono-display ${goodFcst === null ? 'text-ink-faint' : goodFcst ? 'text-signal-ok' : 'text-signal-stop'}`}>
                        {l.delta_vs_forecast_pct === null ? '—' : `${l.delta_vs_forecast_pct > 0 ? '+' : ''}${l.delta_vs_forecast_pct.toFixed(1)}%`}
                      </td>
                      <td className="px-5 py-3 w-[200px]">
                        <ExplainVarianceBubble
                          kpi={{
                            key: `pnl_${l.line.toLowerCase().replace(/\s/g, '_')}`,
                            label: l.line,
                            value: l.value,
                            previous: l.previous,
                            target: l.forecast,
                            format: 'currency',
                          }}
                          range={period}
                          siteLabel={siteLabel}
                          context={sharedContext}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </ReportsLayout>
  );
};

export default FinancialReportsPage;
